'use client';

import * as React from 'react';
import styles from './Toast.module.css';
import clsx from 'clsx';

export interface ToastProps {
  title?: string;
  description?: string;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  action?: {
    label: string;
    onClick: () => void;
  };
  onClose?: () => void;
  duration?: number;
  className?: string;
}

export const Toast: React.FC<ToastProps> = ({
  title,
  description,
  variant = 'default',
  action,
  onClose,
  duration = 5000,
  className,
}) => {
  const [isExiting, setIsExiting] = React.useState(false);
  const isExitingRef = React.useRef(false);
  const closeTimerRef = React.useRef<number | null>(null);

  // Keyed on a ref rather than the isExiting state so requestClose keeps a
  // stable identity through the exit transition; otherwise the auto-dismiss
  // effect below re-runs and schedules a redundant duration timer.
  const requestClose = React.useCallback(() => {
    if (!onClose || isExitingRef.current) return;
    isExitingRef.current = true;
    setIsExiting(true);
    closeTimerRef.current = window.setTimeout(onClose, 150);
  }, [onClose]);

  React.useEffect(
    () => () => {
      if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
    },
    []
  );

  React.useEffect(() => {
    if (duration && onClose) {
      const timer = window.setTimeout(requestClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose, requestClose]);

  const icons: Record<string, React.ReactNode> = {
    success: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M8 12l2.5 2.5L16 9" />
      </svg>
    ),
    warning: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M12 9v4M12 17h.01" />
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
    ),
    error: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M15 9l-6 6M9 9l6 6" />
      </svg>
    ),
    info: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4M12 8h.01" />
      </svg>
    ),
  };

  // A title (or an action button) makes the toast multi-line/asymmetric, so
  // the icon and close button stay top-aligned. With just a one-line
  // description, everything sits on the same line and should be centered.
  const isSingleLine = !title && !action;

  return (
    <div
      className={clsx(
        styles.toast,
        styles[variant],
        isSingleLine && styles.singleLine,
        isExiting && styles.exiting,
        className
      )}
      role="alert"
    >
      {variant !== 'default' && icons[variant] && (
        <div className={styles.icon}>{icons[variant]}</div>
      )}
      <div className={styles.content}>
        {title && <div className={styles.title}>{title}</div>}
        {description && <div className={styles.description}>{description}</div>}
      </div>
      {action && (
        <button className={styles.action} onClick={action.onClick}>
          {action.label}
        </button>
      )}
      {onClose && (
        <button className={styles.close} onClick={requestClose} aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

// Toast container for positioning
export const ToastContainer: React.FC<{
  children: React.ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}> = ({ children, position = 'bottom-right' }) => {
  return (
    <div className={clsx(styles.container, styles[position])}>
      {children}
    </div>
  );
};
